import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Search, Check } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { CollegeLogo } from '../../components/CollegeSearchCombobox';
import '../../college-theme.css';

/* ─── Constants ──────────────────────────────────────────────────────── */

const PLAN_SHORT = {
  ED: 'ED',
  ED_II: 'ED II',
  EA: 'EA',
  REA: 'REA',
  RD: 'RD',
  ROLLING: 'Rolling',
  UCAS: 'UCAS',
  OTHER: 'Other',
};

// Table column widths shared between the header row and every data row.
const GRID_COLUMNS = '48px 40px minmax(220px, 2fr) 168px 68px 132px 78px 108px 104px';
const COLUMN_LABELS = ['Rank', '', 'School', 'Plans offered', 'Admit', 'SAT mid 50%', 'Cost', 'Fit', ''];

/* ─── Helpers ────────────────────────────────────────────────────────── */

function nextJan1() {
  const year = new Date().getFullYear() + 1;
  return `${year}-01-01`;
}

function fmtPercent(v) {
  return v == null ? '—' : `${Math.round(v * 100)}%`;
}

function fmtSatRange(sat25, sat75) {
  if (sat25 == null && sat75 == null) return '—';
  if (sat25 == null) return `≤${sat75}`;
  if (sat75 == null) return `${sat25}+`;
  return `${sat25} to ${sat75}`;
}

function fmtEnrollment(v) {
  return v == null ? null : `${Number(v).toLocaleString()} undergrads`;
}

/* ─── Small presentational pieces ───────────────────────────────────── */

function PlanChip({ plan }) {
  const solid = plan === 'ED';
  return (
    <span
      style={{
        fontSize: 10,
        fontWeight: 600,
        letterSpacing: '0.05em',
        textTransform: 'uppercase',
        padding: '3px 9px',
        borderRadius: 999,
        border: '1px solid var(--college-ink)',
        background: solid ? 'var(--college-ink)' : 'transparent',
        color: solid ? 'var(--college-panel)' : 'var(--college-ink)',
        whiteSpace: 'nowrap',
      }}
    >
      {PLAN_SHORT[plan] || plan}
    </span>
  );
}

function FitBadge({ fit, fitColor, fitBg }) {
  if (!fit) {
    return <span style={{ fontSize: 11, color: 'var(--college-muted-2)' }}>—</span>;
  }
  return (
    <span
      style={{
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        padding: '4px 12px',
        borderRadius: 999,
        background: fitBg,
        color: fitColor,
        whiteSpace: 'nowrap',
      }}
    >
      {fit}
    </span>
  );
}

function ColumnHeader() {
  return (
    <div
      style={{
        display: 'grid', gridTemplateColumns: GRID_COLUMNS, columnGap: 14,
        padding: '0 4px 10px', borderBottom: '2px solid var(--college-ink)', marginBottom: 4,
      }}
    >
      {COLUMN_LABELS.map((h, i) => (
        <span key={i} className="label-editorial" style={{ fontSize: 10 }}>{h}</span>
      ))}
    </div>
  );
}

function Row({ college, onAdd, adding }) {
  return (
    <div
      className="animate-fade-up"
      style={{
        display: 'grid', gridTemplateColumns: GRID_COLUMNS, columnGap: 14, alignItems: 'center',
        padding: '14px 4px', borderBottom: '1px solid var(--college-border-soft)',
      }}
    >
      <span className="font-serif-editorial" style={{ fontSize: 24, color: 'var(--college-muted)' }}>
        {college.rank}
      </span>

      <CollegeLogo schoolUrl={`https://${college.domain}`} name={college.name} size={28} />

      <div style={{ minWidth: 0 }}>
        <Link
          to={`/college/browse/${college.id}`}
          className="font-serif-editorial"
          style={{
            fontSize: 19, color: 'var(--college-ink)', textDecoration: 'none', display: 'block',
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          }}
        >
          {college.name}
        </Link>
        <p style={{ margin: '2px 0 0', fontSize: 12, color: 'var(--college-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {[college.city, college.type, fmtEnrollment(college.enrollment)].filter(Boolean).join(' · ')}
        </p>
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
        {(college.plans || []).map((p) => <PlanChip key={p} plan={p} />)}
      </div>

      <span className="font-mono-editorial" style={{ fontSize: 13, color: 'var(--college-ink-soft)' }}>
        {fmtPercent(college.admissionRate)}
      </span>

      <span className="font-mono-editorial" style={{ fontSize: 13, color: 'var(--college-ink-soft)' }}>
        {fmtSatRange(college.sat25, college.sat75)}
      </span>

      <span className="font-mono-editorial" style={{ fontSize: 13, color: 'var(--college-ink-soft)' }}>
        {college.costShort || '—'}
      </span>

      <FitBadge fit={college.fit} fitColor={college.fitColor} fitBg={college.fitBg} />

      {college.onList ? (
        <span style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color: '#3E6B44', fontWeight: 600, whiteSpace: 'nowrap' }}>
          <Check size={14} /> On list
        </span>
      ) : (
        <button
          type="button"
          onClick={() => onAdd(college)}
          disabled={adding}
          className="font-mono-editorial"
          style={{
            background: 'var(--college-ink)', color: 'var(--college-panel)', border: 'none',
            padding: '7px 14px', fontSize: 12, fontWeight: 600, cursor: adding ? 'default' : 'pointer',
            opacity: adding ? 0.6 : 1, whiteSpace: 'nowrap', width: 'fit-content',
          }}
        >
          {adding ? 'Adding…' : '+ Add'}
        </button>
      )}
    </div>
  );
}

/* ─── Page ───────────────────────────────────────────────────────────── */

export default function CollegeBrowsePage() {
  const { api } = useAuth();
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [colleges, setColleges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);
  const [addingId, setAddingId] = useState(null);
  const firstLoadDone = useRef(false);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query), 300);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => {
    let alive = true;
    if (!firstLoadDone.current) setLoading(true);
    else setSearching(true);
    api.get('/college-browse', { params: { q: debouncedQuery } })
      .then((res) => { if (alive) setColleges(res.data.colleges || []); })
      .catch(() => { if (alive) toast.error('Failed to load colleges'); })
      .finally(() => {
        if (!alive) return;
        setLoading(false);
        setSearching(false);
        firstLoadDone.current = true;
      });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQuery, api, refreshTick]);

  const handleAdd = async (college) => {
    setAddingId(college.id);
    try {
      await api.post('/college-applications', {
        collegeName: college.name,
        domain: college.domain,
        schoolUrl: `https://${college.domain}`,
        city: college.city,
        country: 'US',
        lat: college.lat,
        lng: college.lng,
        plan: 'RD',
        planLabel: 'Regular Decision',
        deadline: nextJan1(),
        scorecardData: {
          admissionRate: college.admissionRate,
          satComposite75: college.sat75,
          satMath25: null,
          satReading25: null,
        },
      });
      toast.success('Added — retag the plan and deadline from My List');
      setRefreshTick((k) => k + 1);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to add school');
    } finally {
      setAddingId(null);
    }
  };

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: '44px 32px 64px' }}>
      {/* Header */}
      <div
        style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap',
          gap: 24, borderBottom: '1px solid var(--college-border)', paddingBottom: 28, marginBottom: 36,
        }}
      >
        <div>
          <h1 className="font-serif-editorial" style={{ fontStyle: 'italic', fontWeight: 400, fontSize: 52, lineHeight: 1, margin: '0 0 10px' }}>
            Browse.
          </h1>
          <p className="font-mono-editorial" style={{ margin: 0, fontSize: 13, color: 'var(--college-muted)' }}>
            All US colleges by US News rank - {colleges.length} shown - fit vs your profile - add straight to your list
          </p>
        </div>

        <div style={{ width: 320 }}>
          <p className="label-editorial" style={{ marginBottom: 6, textAlign: 'right' }}>Search</p>
          <div
            style={{
              display: 'flex', alignItems: 'center', gap: 8, border: '1px solid var(--college-ink)',
              background: 'var(--college-panel)', padding: '0 12px',
            }}
          >
            <Search size={15} color="var(--college-muted)" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by name or city…"
              style={{
                flex: 1, border: 'none', outline: 'none', background: 'transparent',
                padding: '10px 0', fontSize: 13, color: 'var(--college-ink)', fontFamily: 'inherit',
              }}
            />
          </div>
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="font-mono-editorial" style={{ padding: '48px 0', textAlign: 'center', color: 'var(--college-muted)', fontSize: 13 }}>
          Loading colleges…
        </div>
      ) : colleges.length === 0 ? (
        <div style={{ padding: '64px 0', textAlign: 'center', border: '1px dashed var(--college-border)' }}>
          <p className="font-serif-editorial" style={{ fontStyle: 'italic', fontSize: 24, margin: '0 0 8px' }}>
            No colleges match "{query}".
          </p>
          <p style={{ margin: 0, fontSize: 13, color: 'var(--college-muted)' }}>
            Try a different name or city.
          </p>
        </div>
      ) : (
        <div style={{ overflowX: 'auto', opacity: searching ? 0.55 : 1, transition: 'opacity 0.15s ease' }}>
          <div style={{ minWidth: 920 }}>
            <ColumnHeader />
            {colleges.map((college) => (
              <Row key={college.id} college={college} onAdd={handleAdd} adding={addingId === college.id} />
            ))}
          </div>
        </div>
      )}

      {/* Footer caption */}
      <p
        className="font-mono-editorial"
        style={{ marginTop: 48, fontSize: 11, color: 'var(--college-muted-2)', borderTop: '1px solid var(--college-border)', paddingTop: 16 }}
      >
        Rankings: US News 2026 National Universities - stats: College Scorecard (live API) - added schools land in My List tagged RD, retag from there
      </p>
    </div>
  );
}
